/**
 * Team roster, single source of truth for the /team page.
 *
 * Notes:
 *  - Roster and headshots confirmed by Randy on 2026-06-12.
 *  - Dr. Jenkins' middle bio paragraph names partner facilities; the Team page
 *    adapts it before render (do-not-publish rule). Keep the source intact.
 *  - Bios marked TODO[VERIFY] are brief placeholders awaiting real copy
 *    (tracked in PLACEHOLDERS.md).
 */

import jenkins from '../assets/images/Community/jenkins.jpg';
import jakeLeonard from '../assets/images/Community/jake-leonard.jpg';
import jones from '../assets/images/Community/jones.jpg';
import korshin from '../assets/images/Community/korshin.jpg';
import liu from '../assets/images/Community/liu.jpg';
import silver from '../assets/images/Community/silver.jpg';
import wong from '../assets/images/Community/wong.jpg';
import jarosz from '../assets/images/Community/jarosz.jpg';
import lucero from '../assets/images/Community/lucero.jpg';
import sheth from '../assets/images/Community/sheth.jpg';
import yigzaw from '../assets/images/Community/yigzaw.jpg';
import saba from '../assets/images/Community/Saba.jpg';

export const teamMembers = [
  /* ---- Leadership ---- */
  {
    slug: 'randolph-jenkins-md',
    name: 'Randy Jenkins, MD',
    credential: 'MD',
    designation: 'Founder',
    category: 'leadership',
    isFounder: true,
    image: jenkins,
    // Approved bio (2026-06-15). Public-safe, names no specific facilities.
    bio: [
      'Randy Jenkins, MD founded Virginia Rehab Physicians to bring consistent, physician-led rehabilitation medicine to the facilities that need it. He started VRP as a solo practice and grew it into a group known for running rehab units well, with structured medical direction, modern clinical workflows, and dependable on-site physician coverage that hospitals and patients can count on.',
      'A graduate of Wake Forest School of Medicine, Dr. Jenkins completed his internship and residency at Virginia Commonwealth University Health and is board certified in physical medicine and rehabilitation. He is a member of the American Academy of Physical Medicine and Rehabilitation, with clinical interests across general inpatient rehabilitation, neurorehabilitation, and stroke recovery.',
      'The systems behind VRP were built collaboratively, and that is by design. The entire clinical team shaped the workflows, the group worked with hospital leadership to align the documentation standards, and the admission criteria were developed through close collaboration with liaisons, acute care providers, and insurers. The custom software the team relies on was built through engineering and development partnerships, and the billing and credentialing operations were refined with an experienced vendor. Every piece was designed around the PM&R physician’s lifestyle and efficiency, so the physicians who join VRP can care for more patients and have a greater impact.',
      'His clinical leadership centers on the outcomes that matter most in rehabilitation, including higher discharge-to-community rates and lower acute care transfer rates. He serves as a rehabilitation medical director, and under his leadership VRP now holds medical directorship contracts across northern and central Virginia, where he mentors the physicians who join the group so they can practice with real autonomy and real support behind them.',
    ],
    // Short version for any blurb/preview context (not currently rendered).
    shortBio:
      'Randy Jenkins, MD is the founder of Virginia Rehab Physicians. A Wake Forest School of Medicine graduate, he trained at VCU Health and is board certified in physical medicine and rehabilitation, with interests in inpatient rehabilitation, neurorehabilitation, and stroke recovery. He built VRP, with a team he trusts, into a physician-led group known for structured medical direction, strong rehabilitation outcomes, and dependable coverage, with medical directorship contracts across northern and central Virginia.',
  },
  {
    slug: 'jake-leonard-mba',
    name: 'Jake Leonard, MBA',
    credential: 'MBA',
    designation: 'Director of Operations',
    category: 'leadership',
    image: jakeLeonard,
    bio: [
      'Jake Leonard leads operations at Virginia Rehab Physicians, managing financial performance and the systems that keep facility partnerships running smoothly. He focuses on building collaborative relationships with our care partners and driving dependable, efficient delivery of our services.',
      'Jake earned his Bachelor of Arts in Economics from Davidson College and his Master of Business Administration from the University of Southern California. Outside the office he enjoys running, golfing, and travel.',
    ],
  },

  /* ---- Physicians ---- */
  {
    slug: 'aaron-jones-md',
    name: 'Aaron Jones, MD',
    credential: 'MD',
    designation: 'Physician, PM&R',
    category: 'clinical',
    image: jones,
    bio: [
      'Dr. Aaron Jones grew up in San Jose, California, and earned his undergraduate degree in Human Biology at Stanford University. He came to Richmond, Virginia for medical school, completing his internship and residency training at VCU. Dr. Jones has practiced in Richmond for over 15 years, including work with Sheltering Arms Rehabilitation and, more recently, VCU PM&R.',
      'He and his wife, Kathryn, a pediatric radiologist, have two daughters. Dr. Jones is excited to continue his rehabilitation career with the Virginia Rehab Physicians team.',
    ],
  },
  {
    slug: 'timur-korshin-md',
    name: 'Timur Korshin, MD',
    credential: 'MD',
    designation: 'Physician, PM&R',
    category: 'clinical',
    image: korshin,
    // TODO[VERIFY]: replace with a real bio (currently a brief placeholder)
    bio: [
      'Dr. Timur Korshin is a physician specializing in physical medicine and rehabilitation with Virginia Rehab Physicians, providing inpatient rehabilitation care across our units.',
    ],
  },
  {
    slug: 'harry-liu-md',
    name: 'Harry Liu, MD',
    credential: 'MD',
    designation: 'Physician, PM&R',
    category: 'clinical',
    image: liu,
    // TODO[VERIFY]: replace with a real bio (currently a brief placeholder)
    bio: [
      'Dr. Harry Liu is a physician specializing in physical medicine and rehabilitation with Virginia Rehab Physicians, providing inpatient rehabilitation care across our units.',
    ],
  },
  {
    slug: 'timothy-silver-md',
    name: 'Timothy Silver, MD',
    credential: 'MD',
    designation: 'Physician, PM&R',
    category: 'clinical',
    image: silver,
    bio: [
      'Dr. Timothy Silver has been a fixture in the Richmond-area rehabilitation community for over 20 years. After earning his medical degree from East Carolina University School of Medicine, he completed his internship at Yale University–Norwalk Hospital and his residency at the Medical College of Virginia (now VCU School of Medicine).',
      'Dr. Silver has served as Interim Director of the Children’s Hospital Rehabilitation Unit, Chief of Service of Physical Medicine and Rehabilitation at the McGuire Veterans Administration Medical Center, and Medical Director of Sheltering Arms Hospital–South and the Sheltering Arms Institute Neurologic Rehabilitation program. He has provided sports-medicine services for numerous organizations and served as an athlete physician at the 2004, 2006, and 2016 Olympics and Paralympics.',
      'He specializes in all aspects of physical medicine and rehabilitation, with particular interest in sports injuries, neurologic rehabilitation, and administrative consultative work.',
    ],
  },
  {
    slug: 'carmen-wong-md',
    name: 'Carmen Wong, MD',
    credential: 'MD',
    designation: 'Physician, PM&R',
    category: 'clinical',
    image: wong,
    bio: [
      'Dr. Wong is a physician specializing in Physical Medicine and Rehabilitation, driven by helping patients regain function and independence after injury or illness. A graduate of the Virginia Commonwealth University School of Medicine with a residency at the University of Colorado School of Medicine, she brings a diverse background in both outpatient and inpatient rehabilitation.',
      'Her experience includes leading a regenerative and sports-medicine program and providing sports coverage for Richmond-area colleges. Outside of work, Dr. Wong enjoys cooking, reading, and traveling.',
    ],
  },

  /* ---- Advanced practice providers ---- */
  {
    slug: 'andrew-jarosz-np',
    name: 'Andrew Jarosz, NP',
    credential: 'NP',
    designation: 'Nurse Practitioner',
    category: 'clinical',
    image: jarosz,
    bio: [
      'Andrew Jarosz has been a vital part of Virginia Rehab Physicians since its launch, playing a key role in the practice’s growth. He finds great satisfaction in helping patients recover and return home.',
      'Andrew holds both a Bachelor of Nursing and a Master of Nursing – Nurse Practitioner degree from Jefferson College of Health Sciences. With over a decade of healthcare experience, he has served as a Physical Medicine and Rehabilitation Nurse Practitioner, a Hospitalist Nurse Practitioner, and a Surgical/Trauma ICU Registered Nurse.',
    ],
  },
  // Hina Khatri, PA-C removed 2026-06-12, no longer seeing patients with the
  // practice. Entry and photo recoverable from git history if she returns.
  {
    slug: 'lauren-lucero-np',
    name: 'Lauren Lucero, NP',
    credential: 'NP',
    designation: 'Nurse Practitioner',
    category: 'clinical',
    image: lucero,
    bio: [
      'Lauren Lucero is a Family Nurse Practitioner certified by the American Association of Nurse Practitioners. She earned her graduate degree at Virginia Commonwealth University and previously worked as a Registered Nurse in the Neuroscience Intensive Care Unit at VCU Health, caring for critically ill patients with complex neurological and traumatic injuries.',
      'Her bedside experience shaped a clinical interest in patients with neurological conditions and the general inpatient rehabilitation population.',
    ],
  },
  {
    slug: 'hima-sheth-np',
    name: 'Hima Sheth, NP',
    credential: 'NP',
    designation: 'Nurse Practitioner',
    category: 'clinical',
    image: sheth,
    // TODO[VERIFY]: replace with a real bio (currently a brief placeholder)
    bio: [
      'Hima Sheth is a Nurse Practitioner with Virginia Rehab Physicians, providing on-site rehabilitation care and coordinating closely with facility teams to support patient recovery.',
    ],
  },
  {
    slug: 'yalem-yigzaw-np',
    name: 'Yalem Yigzaw, NP',
    credential: 'NP',
    designation: 'Nurse Practitioner',
    category: 'clinical',
    image: yigzaw,
    // TODO[VERIFY]: replace with a real bio (currently a brief placeholder)
    bio: [
      'Yalem Yigzaw is a Nurse Practitioner with Virginia Rehab Physicians, providing on-site rehabilitation care and coordinating closely with facility teams to support patient recovery.',
    ],
  },

  /* ---- Operations (not rendered on /team) ---- */
  {
    slug: 'shaba-m',
    name: 'Shaba M.',
    credential: '',
    designation: 'Administrative Assistant',
    category: 'operations',
    image: saba,
    bio: [
      'Shaba keeps day-to-day operations organized and running smoothly, supporting the clinical team and our facility partners.',
    ],
  },
];

export const getMemberBySlug = (slug) => teamMembers.find((m) => m.slug === slug);
export const clinicalTeam = teamMembers.filter((m) => m.category === 'clinical');
export const physicians = clinicalTeam.filter((m) => m.credential === 'MD');
export const advancedPractice = clinicalTeam.filter((m) => m.credential !== 'MD');
export const leadership = teamMembers.filter((m) => m.category === 'leadership');
export const founder = teamMembers.find((m) => m.isFounder);

/** Initials for the monogram placeholder when a real photo is pending. */
export const initialsFor = (name) =>
  name
    .replace(/,.*$/, '')
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
